import { useState, useEffect, useRef } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { locationAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import RichTextEditor from "@/components/RichTextEditor";
import ImageUpload from "@/components/ImageUpload";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, MapPin, Loader2, Download, Upload, FileSpreadsheet, AlertCircle, CheckCircle, ExternalLink } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLocations = () => {
  const { token } = useAuth();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  
  // Bulk upload state
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    name: "",
    state: "",
    country: "India",
    slug: "",
    description: "",
    image: "",
    meta_title: "",
    meta_description: "",
    canonical_url: "",
    seo_content: "",
    is_popular: false,
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await locationAPI.getAll();
      setLocations(response.data);
    } catch (error) {
      toast.error("Failed to fetch locations");
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/[\s_-]+/g, '-').replace(/^-+|-+$/g, '');
  };

  const resetForm = () => {
    setForm({
      name: "", state: "", country: "India", slug: "", description: "",
      image: "", meta_title: "", meta_description: "", canonical_url: "", seo_content: "", is_popular: false,
    });
    setEditingLocation(null);
  };

  const handleEdit = (location) => {
    setEditingLocation(location);
    setForm({
      name: location.name,
      state: location.state,
      country: location.country || "India",
      slug: location.slug,
      description: location.description || "",
      image: location.image || "",
      meta_title: location.meta_title || "",
      meta_description: location.meta_description || "",
      canonical_url: location.canonical_url || "",
      seo_content: location.seo_content || "",
      is_popular: location.is_popular,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.state || !form.slug) {
      toast.error("Please fill in name, state, and slug");
      return;
    }

    setSaving(true);
    try {
      if (editingLocation) {
        await locationAPI.update(editingLocation.id, form, token);
        toast.success("Location updated");
      } else {
        await locationAPI.create(form, token);
        toast.success("Location created");
      }
      setShowModal(false);
      resetForm();
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save location");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this location?")) return;
    try {
      await locationAPI.delete(id, token);
      toast.success("Location deleted");
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete location");
    }
  };

  const handleSeedPopular = async () => {
    setSeeding(true);
    try {
      const response = await locationAPI.seedPopular(token);
      toast.success(response.data.message);
      fetchLocations();
    } catch (error) {
      toast.error("Failed to seed locations");
    } finally {
      setSeeding(false);
    }
  };

  // Bulk upload handlers
  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error("Please upload a CSV file");
      return;
    }

    setUploading(true);
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/api/locations/bulk-upload`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setUploadResult(response.data.results);
      toast.success(response.data.message);
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload CSV");
      setUploadResult({ errors: [error.response?.data?.detail || "Upload failed"] });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const downloadSampleCSV = () => {
    window.open(`${API_URL}/api/locations/sample-csv`, '_blank');
  };

  return (
    <AdminLayout>
      <div className="space-y-4 sm:space-y-6" data-testid="admin-locations">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <h1 className="text-2xl sm:text-3xl font-semibold text-foreground mb-1">
                Locations
              </h1>
              <p className="text-sm text-muted-foreground">Manage location master for homestays</p>
            </div>
            <div className="flex sm:hidden">
              <Button onClick={() => { resetForm(); setShowModal(true); }} className="flex-1 rounded-full" data-testid="add-location-btn-mobile">
                <Plus className="w-4 h-4 mr-2" />Add Location
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowBulkUpload(true)} className="text-xs sm:text-sm" data-testid="bulk-upload-btn">
              <Upload className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Bulk Upload</span>
            </Button>
            <Button variant="outline" size="sm" onClick={handleSeedPopular} disabled={seeding} className="text-xs sm:text-sm" data-testid="seed-locations-btn">
              {seeding ? <Loader2 className="w-4 h-4 animate-spin sm:mr-2" /> : <Download className="w-4 h-4 sm:mr-2" />}
              <span className="hidden sm:inline">Seed Popular</span>
            </Button>
            <Button onClick={() => { resetForm(); setShowModal(true); }} className="hidden sm:flex rounded-full" data-testid="add-location-btn">
              <Plus className="w-4 h-4 mr-2" />Add Location
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : locations.length > 0 ? (
          <div className="grid gap-3 sm:gap-4">
            {locations.map((location) => {
              const imageUrl = location.image?.startsWith("/") 
                ? `${process.env.REACT_APP_BACKEND_URL}${location.image}` 
                : location.image;
              return (
              <div key={location.id} className="bg-white rounded-xl p-3 sm:p-5 shadow-soft" data-testid={`location-item-${location.id}`}>
                <div className="flex items-start sm:items-center gap-3 sm:gap-4">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {location.image ? (
                      <img src={imageUrl} alt={location.name} className="w-full h-full object-cover" />
                    ) : (
                      <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-foreground text-sm sm:text-base">{location.name}</h3>
                      {location.is_popular && <span className="px-2 py-0.5 bg-accent/10 text-accent text-xs rounded-full">Popular</span>}
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground">{location.state}, {location.country}</p>
                    <p className="text-xs text-muted-foreground mt-1 hidden sm:block">Slug: /{location.slug} • {location.homestay_count} homestays</p>
                    <p className="text-xs text-muted-foreground mt-1 sm:hidden">{location.homestay_count} homestays</p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0 items-center">
                    <a href={`/location/${location.slug}`} target="_blank" rel="noopener noreferrer" className="text-xs text-coconut-green hover:underline flex items-center gap-1" data-testid={`view-location-${location.id}`}><ExternalLink className="w-3 h-3" /> View</a>
                    <Button variant="outline" size="sm" onClick={() => handleEdit(location)} className="h-8 w-8 p-0" data-testid={`edit-location-${location.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDelete(location.id)} className="h-8 w-8 p-0 text-red-500 hover:bg-red-50" data-testid={`delete-location-${location.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )})}
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-12 shadow-soft text-center">
            <MapPin className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No Locations Yet</h3>
            <p className="text-muted-foreground mb-6">Add locations or seed popular destinations</p>
            <Button onClick={handleSeedPopular} disabled={seeding}>
              {seeding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
              Seed Popular Locations
            </Button>
          </div>
        )}

        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0">
            <DialogHeader className="px-6 py-4 border-b bg-gray-50/50 flex-shrink-0">
              <DialogTitle className="text-xl">
                {editingLocation ? "Edit Location" : "Add New Location"}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden" data-testid="location-form">
              <div className="flex-1 overflow-y-auto px-6 py-5">
                <div className="space-y-6">
                  {/* Basic Information Section */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide border-b pb-2">
                      Basic Information
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label className="text-sm font-medium text-gray-700">Name <span className="text-red-500">*</span></label>
                        <Input 
                          value={form.name} 
                          onChange={(e) => { 
                            setForm({ ...form, name: e.target.value }); 
                            if (!editingLocation) setForm(f => ({ ...f, slug: generateSlug(e.target.value) })); 
                          }} 
                          placeholder="e.g., Goa" 
                          className="h-10"
                          data-testid="form-loc-name" 
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-sm font-medium text-gray-700">State <span className="text-red-500">*</span></label>
                        <Input 
                          value={form.state} 
                          onChange={(e) => setForm({ ...form, state: e.target.value })} 
                          placeholder="e.g., Goa" 
                          className="h-10"
                          data-testid="form-loc-state" 
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-sm font-medium text-gray-700">Slug <span className="text-red-500">*</span></label>
                        <Input 
                          value={form.slug} 
                          onChange={(e) => setForm({ ...form, slug: e.target.value })} 
                          placeholder="e.g., goa" 
                          className="h-10"
                          data-testid="form-loc-slug" 
                        />
                        <p className="text-xs text-gray-500">URL path: /{form.slug || 'slug'}</p>
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-sm font-medium text-gray-700">Country</label>
                        <Input 
                          value={form.country} 
                          onChange={(e) => setForm({ ...form, country: e.target.value })} 
                          placeholder="India" 
                          className="h-10"
                        />
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 pt-2">
                      <Checkbox 
                        id="is_popular" 
                        checked={form.is_popular} 
                        onCheckedChange={(c) => setForm({ ...form, is_popular: c })} 
                      />
                      <label htmlFor="is_popular" className="text-sm font-medium text-gray-700 cursor-pointer">
                        Mark as Popular Location
                      </label>
                    </div>
                  </div>

                  {/* Description Section */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide border-b pb-2">
                      Description
                    </h3>
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium text-gray-700">Location Description</label>
                      <p className="text-xs text-gray-500 mb-2">Brief description shown on listings</p>
                      <RichTextEditor 
                        value={form.description} 
                        onChange={(value) => setForm({ ...form, description: value })} 
                        placeholder="Describe this location..." 
                        minHeight="100px" 
                      />
                    </div>
                  </div>

                  {/* Image Section */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide border-b pb-2">
                      Location Image
                    </h3>
                    <ImageUpload
                      value={form.image}
                      onChange={(url) => setForm({ ...form, image: url })}
                      token={token}
                      label=""
                    />
                  </div>

                  {/* SEO Section */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide border-b pb-2">
                      SEO Settings
                    </h3>
                    <div className="space-y-4">
                      <div className="space-y-1.5">
                        <label className="text-sm font-medium text-gray-700">Meta Title</label>
                        <Input 
                          value={form.meta_title} 
                          onChange={(e) => setForm({ ...form, meta_title: e.target.value })} 
                          placeholder="Homestays in Goa - Kokan Homestays" 
                          className="h-10"
                        />
                        <p className="text-xs text-gray-500">Recommended: 50-60 characters</p>
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-sm font-medium text-gray-700">Meta Description</label>
                        <Textarea 
                          value={form.meta_description} 
                          onChange={(e) => setForm({ ...form, meta_description: e.target.value })} 
                          rows={2} 
                          placeholder="Find the best homestays in Goa. Book your perfect coastal getaway with Kokan Homestays."
                          className="resize-none"
                        />
                        <p className="text-xs text-gray-500">Recommended: 150-160 characters</p>
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-sm font-medium text-gray-700">Canonical URL</label>
                        <Input 
                          value={form.canonical_url} 
                          onChange={(e) => setForm({ ...form, canonical_url: e.target.value })} 
                          placeholder="https://kokanhomestays.in/location/... (leave empty for auto)"
                        />
                        <p className="text-xs text-gray-500">Leave blank to use the default page URL</p>
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-sm font-medium text-gray-700">SEO Content (Rich Text)</label>
                        <p className="text-xs text-gray-500 mb-2">Detailed content displayed at the bottom of the location page. Great for SEO.</p>
                        <RichTextEditor 
                          value={form.seo_content} 
                          onChange={(value) => setForm({ ...form, seo_content: value })} 
                          placeholder="Add comprehensive SEO content about this location..."
                          minHeight="150px"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Footer Actions - Fixed at bottom */}
              <div className="flex-shrink-0 px-6 py-4 border-t bg-gray-50/50 flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} data-testid="form-loc-submit">
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {editingLocation ? "Update Location" : "Create Location"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Bulk Upload Modal */}
        <Dialog open={showBulkUpload} onOpenChange={setShowBulkUpload}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5" />
                Bulk Upload Locations
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">CSV Format</h4>
                <p className="text-sm text-blue-800 mb-3">
                  Upload a CSV file with the following columns:
                </p>
                <div className="bg-white rounded p-3 font-mono text-xs overflow-x-auto">
                  <div className="text-gray-500">name,state,country,slug,description,is_popular</div>
                  <div>Alibaug,Maharashtra,India,alibaug,Beautiful coastal town,true</div>
                  <div>Dapoli,Maharashtra,India,dapoli,Serene beaches,false</div>
                </div>
                <div className="mt-3 text-xs text-blue-700">
                  <p><strong>Required:</strong> name, state</p>
                  <p><strong>Optional:</strong> country (default: India), slug (auto-generated), description, is_popular</p>
                </div>
              </div>

              {/* Download Sample */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Download Sample CSV</p>
                  <p className="text-sm text-gray-600">Get a template with example data</p>
                </div>
                <Button variant="outline" onClick={downloadSampleCSV}>
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>

              {/* Upload Area */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary transition-colors">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleBulkUpload}
                  className="hidden"
                  id="csv-upload"
                />
                <label htmlFor="csv-upload" className="cursor-pointer">
                  {uploading ? (
                    <div className="flex flex-col items-center gap-3">
                      <Loader2 className="w-10 h-10 animate-spin text-primary" />
                      <p className="text-muted-foreground">Processing CSV...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-3">
                      <Upload className="w-10 h-10 text-muted-foreground" />
                      <div>
                        <p className="font-medium text-foreground">Click to upload CSV</p>
                        <p className="text-sm text-muted-foreground">or drag and drop</p>
                      </div>
                    </div>
                  )}
                </label>
              </div>

              {/* Upload Results */}
              {uploadResult && (
                <div className="border rounded-lg p-4 space-y-3">
                  <h4 className="font-semibold">Upload Results</h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="p-3 bg-green-50 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-green-600 mx-auto mb-1" />
                      <p className="text-2xl font-bold text-green-600">{uploadResult.created || 0}</p>
                      <p className="text-xs text-green-700">Created</p>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <Pencil className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                      <p className="text-2xl font-bold text-blue-600">{uploadResult.updated || 0}</p>
                      <p className="text-xs text-blue-700">Updated</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <AlertCircle className="w-5 h-5 text-gray-600 mx-auto mb-1" />
                      <p className="text-2xl font-bold text-gray-600">{uploadResult.skipped || 0}</p>
                      <p className="text-xs text-gray-700">Skipped</p>
                    </div>
                  </div>
                  {uploadResult.errors?.length > 0 && (
                    <div className="mt-3 p-3 bg-red-50 rounded-lg">
                      <p className="text-sm font-medium text-red-800 mb-2">Errors:</p>
                      <ul className="text-xs text-red-700 space-y-1 max-h-32 overflow-y-auto">
                        {uploadResult.errors.map((err, i) => (
                          <li key={i}>• {err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminLocations;
